
// eslint:disable-next-line:max-func-body-length
export const timeParser = (
  locale: string,
  use12hr: boolean,
  formatDate: (dt: Date) => string,
  createDate: (
    values?: {
      year?: number;
      month?: number;
      day?: number;
      hours?: number;
      minutes?: number;
      seconds?: number;
    }
  ) => Date
) => {

  const meridiemCache = { am: '', pm: '' };

  const is24HourFormat = () => {
    const timeString = new Date(Date.UTC(2017, 1, 1, 15, 0, 0, 0)).toLocaleTimeString(locale, {timeZone: 'UTC'});
    return !/[APap]\.?M\.?/.test(timeString); // Check for AM/PM markers
  };

  const getAmPmSuffixFromCurrentLocaleIfAny = (): {am: string; pm: string } => {
    
    if (!!meridiemCache.am){
      return meridiemCache;
    }
    
    if (is24HourFormat()) {
      //not using 12hr clock
      return {am: '', pm: ''};
    }

    const dateStringWithAM = new Date(Date.UTC(2017, 1, 1, 0, 0, 0, 0))
                              .toLocaleTimeString(locale, {timeZone: 'UTC'});
    const dateStringWithPM = new Date(Date.UTC(2017, 1, 1, 15, 0, 0, 0))
                              .toLocaleTimeString(locale, {timeZone: 'UTC'});

    //TODO: cleanup and simplify rest of this
    let matchForAM = dateStringWithAM.match(/([^\d:\s\.])/gi)?.slice?.(-2); //to get around empty strings in IE
    let matchForPM = dateStringWithPM.match(/([^\d:\s\.])/gi)?.slice?.(-2); //to get around empty strings in IE
    let am = matchForAM?.[0] && matchForAM?.[1] ? matchForAM?.join('') : '';
    let pm = matchForPM?.[0] && matchForPM?.[1] ? matchForPM?.join('') : '';
    
    if (!am && !/\d/.test(dateStringWithAM)) {
      matchForAM = dateStringWithAM.match(dateStringWithAM.substring(0, dateStringWithAM.indexOf(' ')));
      matchForPM = dateStringWithPM.match(dateStringWithAM.substring(0, dateStringWithPM.indexOf(' ')));

      const index = dateStringWithAM.indexOf(' ', dateStringWithAM.indexOf(' '));
      if (index >= 0) {
        am = dateStringWithAM.substr(index + 1);
        pm = dateStringWithPM.substr(index + 1);
      }
    }
    meridiemCache.am = am;
    meridiemCache.pm = pm;
    return meridiemCache;
  };

  const parseTimeStringAs24Hr = (timeString: string) => {
    const time = timeString.match(/^\s*?(\d?\d):?\s?(\d?\d?)/);
    if (timeString.length === 0 || !time) {
      return [undefined, undefined];
    }

    let hours = Number(time[1]);
    let minutes = Number(time[2]) || 0;

    //add +1 to hour if minute parsed input exceeds 60 limit
    hours = minutes >= 60 ? hours + 1 : hours;
    if(hours >= 24) {
      hours = 0;
    }
    minutes = minutes >= 60 ? 0 : minutes;

    return [hours, minutes];
  };

  /**
   * @private 12 hr clock parsing
   *
   */
  const parseTimeStringAs12Hr = (timeString: string) => {
    let time = String(timeString).match(/^\W*?(\d?\d):?\s?(\d?\d?)?\W*?/i);
    const {am, pm} = getAmPmSuffixFromCurrentLocaleIfAny();
    const defaultMeridian = pm;

    if (!time) {
      return [undefined, undefined];
    }
    let hours = Number(time[1]);
    let minutes = Number(time[2]) || 0;

    //TODO: This. is. crazy. Unreadable.
    const ampm =
      timeString.includes(am) ||
      timeString.includes(am.toLowerCase())
        ? am
        : timeString.includes(pm) ||
          timeString.includes(pm.toLowerCase())
          ? pm
          : defaultMeridian;

    if (hours > 12) {
      // if input is in different format e.g., 'hm, or hmm, etc.' than standard 'hh:mm'
      time = timeString.match(/(\d)(\d?\d?)/);
      minutes = Number(time[2]) || 0;
      //add +1 to hour if minute parsed input exceeds 60 limit
      hours = minutes > 60 ? Number(time[1]) + 1 : Number(time[1]);
    }
    minutes = minutes > 60 ? 0 : minutes;

    return [hours, minutes, ampm];
  };

  const addHoursAndReturnHours: any = (hours: number, operator: string) => {

    const now = createDate();

    if (operator === '+') {
      now.setUTCHours(now.getUTCHours() + hours);
      return now.getUTCHours();
    }
    if (operator === '-') {
      now.setUTCHours(now.getUTCHours() - hours);
      return now.getUTCHours();
    } 

    return now.getUTCHours();
    
  };

  //TODO: what is the meaning of this method?
  const addHoursAndReturnHoursAndMeridien = (addHour: number) => {
    addHour = addHour ? addHour : 0;

    const time = createDate(); 
    let AM = true;
    let ndble = 0;
    let hours;
    let newHour;
    let overHour;

    //TODO: there is a function here for this, convert12To24
    //change form 24 to 12 hour clock
    if (time.getUTCHours() >= 13) {
      hours = time.getUTCHours() - 12;
      AM = hours > 12 ? true : false;
    } else {
      hours = time.getUTCHours();
      AM = hours > 12 ? false : true;
    }
    //set hour
    if (hours + addHour >= 13 || hours + addHour <= 0) {
      overHour = (hours + addHour) % 12;
      ndble = Math.floor(Math.abs((hours + addHour) / 12));
      if (overHour <= 0) {
        newHour = overHour + 12;
        if (overHour === 0) {
          ndble++;
        }
      } else {
        if (overHour === 0) {
          newHour = 12;
          ndble++;
        } else {
          ndble++;
          newHour = overHour;
        }
      }
      AM = (ndble + 1) % 2 === 0 ? AM : !AM;
    } else {
      AM = hours + addHour === 12 ? !AM : AM;
      newHour = hours + addHour;
    }
    const {am, pm} = getAmPmSuffixFromCurrentLocaleIfAny();
    const ampm = AM ? am : pm;
    return [newHour, ampm];
  };

  /**
   * @private Shortcuts for time
   *
   */
  const addHoursFromUserShortcut = (
    shortcut: string[],
    use24HourTime: boolean
  ) : any[] => {
    if (!shortcut) {
      return undefined;
    }
    
    const operator = shortcut[1];
    const hours = Number(shortcut[2]);
    const now = createDate();
    const minutes = now.getUTCMinutes();

    if (shortcut[0] && !operator && !hours) {
      
      if (use24HourTime) {
        const hoursR = addHoursAndReturnHours(0, '');
        return [hoursR, minutes];
      } else {
        const hoursR = addHoursAndReturnHoursAndMeridien(0)[0];
        const ttPart = addHoursAndReturnHoursAndMeridien(0)[1];
        
        return [hoursR, minutes, ttPart];
      }
    }
    if (operator === '+') {
      if (use24HourTime) {
        const hoursR = addHoursAndReturnHours(hours, '+');
        return [hoursR, minutes];
      } else {
        const hoursR = addHoursAndReturnHoursAndMeridien(hours)[0];
        const ttPart = addHoursAndReturnHoursAndMeridien(hours)[1];
        return [hoursR, minutes, ttPart];
      }
    }
    if (operator === '-') {
      if (use24HourTime) {
        const hoursR = addHoursAndReturnHours(hours, '-');
        return [hoursR, minutes];
      } else {
        const hoursR = addHoursAndReturnHoursAndMeridien(-hours)[0];
        const ttPart = addHoursAndReturnHoursAndMeridien(-hours)[1];
        return [hoursR, minutes, ttPart];
      }
    } else {
      return undefined;
    }
  };

  const parseStringAndReturnHhMMAndSuffix = (time24hr: string) => {
    const time12hr = { hours: 0, minutes: 0, ampm: '' };
    const hours24 = Number(time24hr.split(':')[0]);
    const {am, pm} = getAmPmSuffixFromCurrentLocaleIfAny();

    time12hr.hours = (hours24 + 11) % 12 + 1;
    time12hr.minutes = Number(time24hr.split(':')[1]);
    time12hr.ampm = hours24 >= 12 ? pm : am;

    return time12hr;
  };

  const convert24To12 = (hours: number) => {
    return (hours + 11) % 12 + 1;
  };

  const convert12To24 = (
    hours: number,
    minutes: number,
    meridian: string
  ):  number => {
    const {am, pm} = getAmPmSuffixFromCurrentLocaleIfAny();

    if (!am){
      return hours;
    }

    let hours24 = hours;
    if (meridian === pm && hours < 12) {
      hours24 = hours + 12;
    } else if (meridian === am && hours === 12) {
      hours24 = 0;
    }

    return hours24;
  };

  const parseStringAs12HoursAndReturnHours = (userInput: string) => {
    return parseStringAndReturnHhMMAndSuffix(userInput).hours;
  };

  const getAmPmSuffixesFromUserInput = (userInput: string) => {
    return use12hr ? parseStringAndReturnHhMMAndSuffix(userInput).ampm : '';
  };

  const parseTimeInputOrUserShortcut: any = (input: string) => {
    const isShortcut = String(input).match(/\b([h|n|t])(\+|\-)?(\d\d?)?\s?/i);
    const calculatedTime =
      isShortcut && addHoursFromUserShortcut(isShortcut.slice(1), !use12hr);
    const parsedTime =
      !isShortcut &&
      input &&
      (!use12hr ? parseTimeStringAs24Hr(input) : parseTimeStringAs12Hr(input));

    return calculatedTime || parsedTime || undefined;
  };

  //TODO: there is no reason these functions should return anything but UTC date objects
  //TODO: fix duplicates below
  return {
    parseTimeInputOrUserShortcut,
    parseStringAs12HoursAndReturnHours,
    convert12To24,
    convert24To12, 
    parseStringAndReturnHhMMAndSuffix,
    addHoursFromUserShortcut,
    addHoursAndReturnHoursAndMeridien,
    addHoursAndReturnHours,
    parseTimeStringAs12Hr,
    parseTimeStringAs24Hr,
    getAmPmSuffixesFromUserInput, 
    getAmPmSuffixFromCurrentLocaleIfAny
  };
};
